import React from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Shield, Hammer, Crosshair, Megaphone, ScrollText, Lock, Search } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

const HERO_BG = 'https://images.unsplash.com/photo-1440635592348-167b1b30296f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';

export default function Landing() {
  return (
    <div className="min-h-screen bg-plai-bg text-white" data-testid="landing-page">
      {/* Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 backdrop-blur-md bg-black/50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" />
          <div className="flex items-center gap-3">
            <Link to="/ledger" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white" data-testid="nav-ledger-link">Ledger</Link>
            <Link to="/auth" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white" data-testid="nav-login-link">Log In</Link>
            <Link to="/auth?mode=signup" className="btn-primary-cta text-xs px-4 py-2" data-testid="nav-signup-cta">Secure My Land</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[100vh] flex items-center justify-center px-6 pt-24" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-plai-bg" />
        </div>
        <div className="absolute inset-0 grain-overlay z-0" />

        <div className="relative z-10 max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 mb-6 border border-white/15 px-4 py-2 bg-black/40">
            <Shield className="h-4 w-4 text-plai-teal" strokeWidth={1.5} />
            <span className="font-body text-[10px] tracking-[0.3em] uppercase text-white/70">DEFENDING YOUR RIGHT TO OWN THE LAND<sup>TM</sup></span>
          </div>

          <h1 className="font-headings text-5xl sm:text-7xl lg:text-8xl uppercase tracking-tighter leading-[0.9] text-white" data-testid="hero-headline">
            Your Land.<br/>
            Your Voice.<br/>
            <span className="iridescent-text">Our Tech.</span>
          </h1>

          <p className="font-body text-base sm:text-lg text-white/75 mt-8 max-w-2xl mx-auto leading-relaxed">
            Stop the land-grabs. Halt the closures. Public Landowner AI<sup>TM</sup> puts massive research and drafting power in your pocket for the price of a burrito.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth?mode=signup" className="btn-primary-cta text-lg px-10 py-5" data-testid="hero-primary-cta">
              Secure My Land &mdash; $9.99/MO
            </Link>
            <Link to="#how" className="btn-ghost" data-testid="hero-secondary-cta">See How It Works</Link>
          </div>

          <p className="font-body text-[10px] tracking-[0.25em] uppercase text-white/40 mt-6">
            Cancel anytime. No long contracts. Read disclaimers before generating.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 px-6 max-w-7xl mx-auto" data-testid="mission-section">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="tactical-card p-8" data-testid="mission-card-1">
            <Crosshair className="h-7 w-7 text-plai-teal mb-4" strokeWidth={1.5} />
            <h3 className="font-headings text-2xl uppercase mb-3">Government-Only Focus</h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">We target Government Secretaries and Directors. Not your neighbor. You punch up at the system.</p>
          </div>
          <div className="tactical-card p-8" data-testid="mission-card-2">
            <Shield className="h-7 w-7 text-plai-gold mb-4" strokeWidth={1.5} />
            <h3 className="font-headings text-2xl uppercase mb-3">Advocacy Shield</h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">In 2026, more states protect citizens who speak up. Drafting a Notice is a First Amendment right.</p>
          </div>
          <div className="tactical-card p-8" data-testid="mission-card-3">
            <Hammer className="h-7 w-7 text-plai-purple mb-4" strokeWidth={1.5} />
            <h3 className="font-headings text-2xl uppercase mb-3">The Moat</h3>
            <p className="font-body text-sm text-white/70 leading-relaxed">Bureaucracy is the weapon. An informational notice forces a 60-day review. That friction protects the land.</p>
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="py-24 px-6 max-w-7xl mx-auto" data-testid="how-section">
        <div className="text-center mb-14">
          <p className="font-body text-xs tracking-[0.3em] uppercase text-white/50 mb-3">The Glossy Sledgehammer</p>
          <h2 className="font-headings text-4xl sm:text-5xl uppercase tracking-tight">Six Agents. One Verdict.</h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Search, label: 'Legislative Researcher', body: 'Maps your harm to live 2026 statutes. Antiquities Act. NEPA. ESA. State and local codes.' },
            { icon: Shield, label: 'Standing Validator', body: 'Confirms your legal standing. Asks follow-ups so your case is bulletproof.' },
            { icon: Lock, label: 'Procedural Specialist', body: 'Hunts for "arbitrary and capricious" decisions and missed comment windows.' },
            { icon: Mountain, label: 'Eco-Scientist', body: 'Pulls live 2026 species data. Bear corridors. Bat habitat. Dark sky impact.' },
            { icon: Crosshair, label: 'Opposition Analyst', body: 'Plays the government attorney. Attacks your draft so you can fix it before they do.' },
            { icon: ScrollText, label: 'Plain-Language Translator', body: 'Rewrites the final notice so a 10-year-old can read it. Powerful and clear.' },
          ].map((a, i) => (
            <div key={i} className="tactical-card p-6 hover:border-plai-teal/40 transition-colors" data-testid={`agent-card-${i}`}>
              <a.icon className="h-6 w-6 text-plai-teal mb-3" strokeWidth={1.5} />
              <h4 className="font-headings text-xl uppercase mb-2">{a.label}</h4>
              <p className="font-body text-xs text-white/60 leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Output */}
      <section className="py-24 px-6 max-w-7xl mx-auto" data-testid="outputs-section">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass iridescent-border p-10">
            <ScrollText className="h-8 w-8 text-plai-cta mb-4" strokeWidth={1.5} />
            <h3 className="font-headings text-3xl uppercase mb-3">The Sledgehammer PDF</h3>
            <p className="font-body text-sm text-white/75 leading-relaxed">A time-stamped Draft Notice of Intent. Standing Statement. Cited violations. Filing instructions to the right Secretary in D.C.</p>
          </div>
          <div className="glass iridescent-border p-10">
            <Megaphone className="h-8 w-8 text-plai-purple mb-4" strokeWidth={1.5} />
            <h3 className="font-headings text-3xl uppercase mb-3">The Megaphone Post</h3>
            <p className="font-body text-sm text-white/75 leading-relaxed">A high-impact Instagram caption. Thirty surgical hashtags. Tags the right official. Built to go viral.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center" data-testid="bottom-cta-section">
        <h2 className="font-headings text-4xl sm:text-6xl uppercase tracking-tight mb-4">Take Back The Land.</h2>
        <p className="font-body text-white/60 text-base mb-8 max-w-xl mx-auto">For the price of a burrito. Cancel anytime.</p>
        <Link to="/auth?mode=signup" className="btn-primary-cta text-lg px-10 py-5" data-testid="bottom-cta-btn">Secure My Land &mdash; $9.99/MO</Link>
      </section>

      <Footer />
    </div>
  );
}
